/**
 * useInterviewCycleSettings.ts
 *
 * V55.61: 면담주기 설정 훅
 *
 * @module shared/hooks/useInterviewCycleSettings
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useCurrentUser } from './useCurrentUser';

// ============================================
// 타입 정의
// ============================================

/** 주기 값 (월 단위, null=없음) */
export type CycleMonths = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | null;

/** 면담주기 설정 */
export interface InterviewCycleSettings {
  cycleProbation: CycleMonths;
  cycleDispatch: CycleMonths;
  cycleContract: CycleMonths;
  cycleIntern: CycleMonths;
  cycleRegular: CycleMonths;
  probationAlertEnabled: boolean;
}

/** 기본 설정값 */
export const DEFAULT_CYCLE_SETTINGS: InterviewCycleSettings = {
  cycleProbation: null,
  cycleDispatch: null,
  cycleContract: null,
  cycleIntern: null,
  cycleRegular: null,
  probationAlertEnabled: false,
};

/** 면담관리 상태 타입 (면담주기 설정 기반 계산 결과) */
export type InterviewStatusType = 'none' | 'completed' | 'upcoming' | 'overdue';

/** 면담관리 상태 표시 정보 */
export interface InterviewStatusDisplay {
  status: InterviewStatusType;
  label: string;
  dDayText: string | null;
  daysRemaining: number | null;
  isProbationAlert: boolean;
}

/** 마지막 면담상태 (가장 최근 면담 1건의 전송/합의 상태) */
export type DeliveryStatus = 'notSent' | 'sent' | 'agreed' | 'disagreed';

/**
 * 직원별 면담 상태
 *
 * - status: 면담관리 상태 (면담주기 설정 기반 계산 결과)
 * - lastDeliveryStatus: 마지막 면담상태 (가장 최근 면담 1건의 deliveryStatus)
 */
export interface EmployeeInterviewStatus {
  employeeId: string;
  lastInterviewDate: string | null;
  interviewCount: number;
  status: InterviewStatusDisplay;               // 면담관리 상태 (주기 기반)
  lastDeliveryStatus: DeliveryStatus | null;    // 마지막 면담상태 (최근 1건)
  hasProbationAgreement: boolean;               // V62: 수습면담 상호합의 완료 여부
}

// ============================================
// useInterviewCycleSettings 훅
// ============================================

interface UseInterviewCycleSettingsResult {
  settings: InterviewCycleSettings;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  saveSettings: (newSettings: InterviewCycleSettings) => Promise<void>;
  isSaving: boolean;
}

/**
 * 면담주기 설정 조회/저장 훅
 */
export function useInterviewCycleSettings(): UseInterviewCycleSettingsResult {
  const { user } = useCurrentUser();

  const [settings, setSettings] = useState<InterviewCycleSettings>(DEFAULT_CYCLE_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 설정 조회
  const fetchSettings = useCallback(async () => {
    if (!user || !functions) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getSettings = httpsCallable<
        Record<string, never>,
        { success: boolean; settings: InterviewCycleSettings | null }
      >(functions, 'getInterviewCycleSettings');

      const result = await getSettings({});

      if (result.data.success) {
        setSettings(result.data.settings || DEFAULT_CYCLE_SETTINGS);
      } else {
        throw new Error('면담주기 설정 조회 실패');
      }
    } catch (err: any) {
      console.error('[V55.61] useInterviewCycleSettings fetch error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // 설정 저장
  const saveSettings = useCallback(async (newSettings: InterviewCycleSettings) => {
    if (!functions) {
      throw new Error('Firebase Functions가 초기화되지 않았습니다.');
    }

    setIsSaving(true);

    try {
      const setSettingsFn = httpsCallable<InterviewCycleSettings, { success: boolean }>(
        functions,
        'setInterviewCycleSettings'
      );

      const result = await setSettingsFn(newSettings);

      if (result.data.success) {
        setSettings(newSettings);
      } else {
        throw new Error('면담주기 설정 저장 실패');
      }
    } catch (err: any) {
      console.error('[V55.61] useInterviewCycleSettings save error:', err);
      throw err instanceof Error ? err : new Error(err.message || 'Unknown error');
    } finally {
      setIsSaving(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    error,
    refetch: fetchSettings,
    saveSettings,
    isSaving,
  };
}

// ============================================
// useEmployeeInterviewStatus 훅
// ============================================

interface UseEmployeeInterviewStatusResult {
  statuses: Map<string, EmployeeInterviewStatus>;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * 직원 목록의 면담 상태 조회 훅
 *
 * @param employeeIds 직원 ID 목록
 */
export function useEmployeeInterviewStatus(
  employeeIds: string[]
): UseEmployeeInterviewStatusResult {
  const { user } = useCurrentUser();

  const [statuses, setStatuses] = useState<Map<string, EmployeeInterviewStatus>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStatuses = useCallback(async () => {
    if (!user || !functions || employeeIds.length === 0) {
      setStatuses(new Map());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const getStatus = httpsCallable<
        { employeeIds: string[] },
        { success: boolean; statuses: EmployeeInterviewStatus[] }
      >(functions, 'getEmployeeInterviewStatus');

      const result = await getStatus({ employeeIds });

      if (result.data.success) {
        const newMap = new Map<string, EmployeeInterviewStatus>();
        result.data.statuses.forEach((s) => {
          newMap.set(s.employeeId, s);
        });
        setStatuses(newMap);
      } else {
        throw new Error('면담 상태 조회 실패');
      }
    } catch (err: any) {
      console.error('[V55.61] useEmployeeInterviewStatus error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [user, employeeIds.join(',')]); // employeeIds 변경 시 재조회

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  return {
    statuses,
    isLoading,
    error,
    refetch: fetchStatuses,
  };
}

// ============================================
// 상수
// ============================================

/** 주기 옵션 (드롭다운용) */
export const CYCLE_OPTIONS: { value: CycleMonths; label: string }[] = [
  { value: null, label: '없음' },
  { value: 1, label: '1개월' },
  { value: 2, label: '2개월' },
  { value: 3, label: '3개월' },
  { value: 4, label: '4개월' },
  { value: 5, label: '5개월' },
  { value: 6, label: '6개월' },
  { value: 7, label: '7개월' },
  { value: 8, label: '8개월' },
  { value: 9, label: '9개월' },
  { value: 10, label: '10개월' },
  { value: 11, label: '11개월' },
  { value: 12, label: '12개월' },
];

/** 고용형태 라벨 */
export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  probation: '수습사원',
  DISPATCH: '파견직',
  CONTRACT: '계약직',
  INTERN: '인턴',
  REGULAR: '정규직',
};

/** 상태 스타일 */
export const INTERVIEW_STATUS_STYLES: Record<
  InterviewStatusType,
  { color: string; bg: string; label: string }
> = {
  none: {
    color: 'var(--color-gray-500)',
    bg: 'var(--color-gray-100)',
    label: '미진행',
  },
  completed: {
    color: 'var(--color-green-600)',
    bg: 'var(--color-green-50)',
    label: '면담완료',
  },
  upcoming: {
    color: 'var(--color-orange-600)',
    bg: 'var(--color-orange-50)',
    label: '면담임박',
  },
  overdue: {
    color: 'var(--color-red-600)',
    bg: 'var(--color-red-50)',
    label: '면담지연',
  },
};
